import { usePostHog } from 'posthog-js/react';

import { Fragment, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Navigate,
  Outlet,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { AppLoader } from '@ingrid/luterkort-react';

import { useIntendedUrl } from '~src/main/hooks/useIntendedUrl';
import { useTracking } from '~src/main/hooks/useTracking';
import { AppAuthError } from '~src/pages/app/app-auth-error';
import { AppErrorPage } from '~src/pages/app/app-error';
import { AppExpiredPage } from '~src/pages/app/app-expired';
import { AppLoggedOutPage } from '~src/pages/app/app-logged-out';
import { AppLoginPage } from '~src/pages/app/app-login';
import { AppLoginCallback } from '~src/pages/app/app-login-callback';
import { Page as DashboardPage } from '~src/pages/dashboard/page';
import { useDetailedMerchantListQuery } from '~src/pages/merchant/queries';
import { OrderCreateShipmentPage } from '~src/pages/order/create-shipment/page';
import { Page as OrderDetailsPage } from '~src/pages/order/details/page';
import { Page as OrderListPage } from '~src/pages/order/list/page';
import { Page as AddressBookCreatePage } from '~src/pages/settings/address-book/create/page';
import { Page as AddressBookDuplicatePage } from '~src/pages/settings/address-book/duplicate/page';
import { Page as AddressBookEditPage } from '~src/pages/settings/address-book/edit/page';
import { Page as AddressBookListPage } from '~src/pages/settings/address-book/list/page';
import { Page as BookingRuleConfiguratorEditPage } from '~src/pages/settings/booking-rule-configurator/edit/page';
import { Page as BookingRuleConfiguratorListPage } from '~src/pages/settings/booking-rule-configurator/list/page';
import { Page as BookingRuleDetailsPage } from '~src/pages/settings/booking-rule/details/page';
import { Page as BookingRuleListPage } from '~src/pages/settings/booking-rule/list/page';
import { DuplicatePage as CarrierContractDuplicatePage } from '~src/pages/settings/carrier-contract/create/duplicate-page';
import { Page as CarrierContractCreatePage } from '~src/pages/settings/carrier-contract/create/page';
import { Page as CarrierContractDetailsPage } from '~src/pages/settings/carrier-contract/details/page';
import { Page as CarrierContractEditPage } from '~src/pages/settings/carrier-contract/edit/page';
import { Page as CarrierContractListPage } from '~src/pages/settings/carrier-contract/list/page';
import { Page as SettingsPage } from '~src/pages/settings/page';
import { Page as PendingShipmentListPage } from '~src/pages/settings/pending-shipment/list/page';
import { DuplicatePage as ShipmentTemplateDuplicatePage } from '~src/pages/settings/shipment-template/create/duplicate-page';
import { Page as ShipmentTemplateCreatePage } from '~src/pages/settings/shipment-template/create/page';
import { DetailsPage as ShipmentTemplateDetailsPage } from '~src/pages/settings/shipment-template/details/page';
import { Page as ShipmentTemplateEditPage } from '~src/pages/settings/shipment-template/edit/page';
import { Page as ShipmentTemplateListPage } from '~src/pages/settings/shipment-template/list/page';
import { CreateReturnPage as ShipmentCreateReturnPage } from '~src/pages/shipment/create/create-return-page';
import { DuplicatePage as ShipmentDuplicatePage } from '~src/pages/shipment/create/duplicate-page';
import { CreatePage as ShipmentCreatePage } from '~src/pages/shipment/create/page';
import { PendingPage as ShipmentCreateWithPendingPage } from '~src/pages/shipment/create/pending-page';
import { DetailsPage as ShipmentDetailsPage } from '~src/pages/shipment/details/page';
import { EditPage as ShipmentEditPage } from '~src/pages/shipment/edit/page';
import { Page as ShipmentListPage } from '~src/pages/shipment/list/page';

import { RouteWithRole } from '../components/RouteWithRole/RouteWithRole';
import { AppLayout } from './app-layout/app-layout';
import { AppRedirect } from './app-layout/app-redirect';
import { useSiteId } from './hooks/useSiteId';
import { AppRoute } from './routes';

function RouteWithIdentityTracking() {
  const tracking = useTracking();
  const auth = useAuth();

  if (auth.user?.profile) {
    tracking.identify(auth.user.profile.sub, auth.user.profile.email);
  }

  return <Outlet />;
}

function RouteWithAuth() {
  const intended = useIntendedUrl();
  const navigate = useNavigate();
  const auth = useAuth();

  if (auth.isLoading) {
    return <AppLoader />;
  }

  if (auth.error) {
    return <AppAuthError error={auth.error} />;
  }

  if (!auth.isAuthenticated) {
    intended.set();
    navigate(AppRoute.LOGIN);
    return null;
  }

  return <RouteWithIdentityTracking />;
}

function RouteWithSite() {
  const { merchants } = useDetailedMerchantListQuery();
  const siteId = useSiteId();

  /**
   * Given that the site id is something the user is not authorized for, we should
   * instead redirect the user back to "/", which would either directly redirect
   * them to the only site they have access to, or let them choose.
   */
  if (siteId && !merchants.find(m => m.sites.some(s => s.id === siteId))) {
    return <Navigate to="/" />;
  }

  return <AppLayout />;
}

function RouteWithPageviewTracking() {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    posthog.capture('$pageview');
  }, [location, posthog]);

  return <Outlet />;
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RouteWithPageviewTracking />} errorElement={<AppErrorPage />}>
      <Route element={<AppLoggedOutPage />} path={AppRoute.LOGGED_OUT} />
      <Route element={<AppExpiredPage />} path={AppRoute.EXPIRED} />

      <Route path={AppRoute.LOGIN}>
        <Route path="" element={<AppLoginPage />} />
        <Route path=":customer" element={<AppLoginPage />} />
      </Route>

      <Route element={<RouteWithAuth />} path={AppRoute.ROOT}>
        <Route element={<AppRedirect />} path="" />
        <Route element={<AppLoginCallback />} path={AppRoute.LOGIN_CALLBACK} />

        <Route element={<AppLayout />}>
          <Route element={<RouteWithRole roles={['transport:view']} />}>
            <Route path={AppRoute.SITE} element={<Fragment />} />
          </Route>
        </Route>

        <Route element={<RouteWithSite />} path={`${AppRoute.SITE}/:siteId`}>
          <Route element={<Navigate to={AppRoute.SHIPMENTS} />} path="" />

          <Route element={<RouteWithRole roles={['transport:view']} />}>
            <Route path={AppRoute.DASHBOARD}>
              <Route element={<DashboardPage />} path="" />
            </Route>
          </Route>

          <Route path={AppRoute.SETTINGS}>
            <Route element={<RouteWithRole roles={['transport:view']} />}>
              <Route element={<SettingsPage />} path="" />
            </Route>

            <Route element={<RouteWithRole roles={['transport:view']} />}>
              <Route path={AppRoute.PENDING_SHIPMENTS}>
                <Route element={<PendingShipmentListPage />} path="" />
              </Route>
            </Route>

            <Route path={AppRoute.ADDRESS_BOOK}>
              <Route element={<RouteWithRole roles={['transport:view']} />}>
                <Route element={<AddressBookListPage />} path="" />
              </Route>

              <Route element={<RouteWithRole roles={['transport:view', 'transport:edit']} />}>
                <Route element={<AddressBookCreatePage />} path={AppRoute.CREATE} />
                <Route element={<AddressBookDuplicatePage />} path={`${AppRoute.DUPLICATE}/:addressBookID`} />
                <Route element={<AddressBookEditPage />} path={`${AppRoute.EDIT}/:addressBookID`} />
              </Route>
            </Route>

            <Route path={AppRoute.CARRIER_CONTRACTS}>
              <Route element={<RouteWithRole roles={['transport:view']} />}>
                <Route element={<CarrierContractListPage />} path="" />
                <Route element={<CarrierContractDetailsPage />} path=":id" />
              </Route>

              <Route element={<RouteWithRole roles={['transport:edit']} />}>
                <Route element={<CarrierContractCreatePage />} path={AppRoute.CREATE} />
                <Route element={<CarrierContractDuplicatePage />} path={`${AppRoute.DUPLICATE}/:id`} />
                <Route element={<CarrierContractEditPage />} path={`${AppRoute.EDIT}/:id`} />
              </Route>
            </Route>

            <Route path={AppRoute.SHIPMENT_TEMPLATES}>
              <Route element={<RouteWithRole roles={['transport:view']} />}>
                <Route element={<ShipmentTemplateListPage />} path="" />
                <Route element={<ShipmentTemplateDetailsPage />} path=":id" />
              </Route>

              <Route element={<RouteWithRole roles={['transport:view', 'transport:edit']} />}>
                <Route element={<ShipmentTemplateCreatePage />} path={AppRoute.CREATE} />
                <Route element={<ShipmentTemplateDuplicatePage />} path={`${AppRoute.DUPLICATE}/:id`} />
                <Route element={<ShipmentTemplateEditPage />} path={`${AppRoute.EDIT}/:id`} />
              </Route>
            </Route>

            <Route element={<RouteWithRole roles={['transport:view']} />}>
              <Route path={AppRoute.BOOKING_RULES}>
                <Route element={<BookingRuleListPage />} path="" />
                <Route element={<BookingRuleDetailsPage />} path=":method" />
              </Route>

              <Route path={AppRoute.BOOKING_RULE_CONFIGURATOR}>
                <Route element={<BookingRuleConfiguratorListPage />} path="" />

                <Route element={<RouteWithRole roles={['transport:edit']} />}>
                  <Route element={<BookingRuleConfiguratorEditPage />} path={`${AppRoute.EDIT}`} />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path={AppRoute.SHIPMENTS}>
            <Route element={<RouteWithRole roles={['transport:view']} />}>
              <Route element={<ShipmentListPage />} path="" />
              <Route element={<ShipmentDetailsPage />} path=":id" />
            </Route>

            <Route element={<RouteWithRole roles={['transport:view', 'transport:edit']} />}>
              <Route element={<ShipmentCreatePage />} path={AppRoute.CREATE} />
              <Route
                element={<ShipmentCreateWithPendingPage />}
                path={`${AppRoute.CREATE}/${AppRoute.PENDING}/:lookup_id`}
              />

              <Route element={<ShipmentEditPage />} path={`${AppRoute.EDIT}/:id`} />
              <Route element={<ShipmentDuplicatePage />} path={`${AppRoute.DUPLICATE}/:id`} />
              <Route element={<ShipmentCreateReturnPage />} path={`${AppRoute.RETURN}/:id`} />
            </Route>
          </Route>

          <Route path={AppRoute.TRANSPORT_ORDERS}>
            <Route element={<RouteWithRole roles={['transport:view']} />}>
              <Route element={<OrderListPage />} path="" />
              <Route element={<OrderDetailsPage />} path=":orderId" />
            </Route>

            <Route element={<RouteWithRole roles={['transport:view', 'transport:edit']} />}>
              <Route element={<OrderCreateShipmentPage />} path={`:orderId/${AppRoute.SHIPMENTS_CREATE}`} />
            </Route>
          </Route>
        </Route>
      </Route>
    </Route>
  )
);
